import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link, useParams } from "react-router-dom";
import axios from "axios";
import { 
  FaRupeeSign,
  FaShoppingCart,
  FaArrowLeft,
  FaArrowRight,
  FaCheckCircle,
  FaTruck,
  FaShieldAlt,
  FaExchangeAlt,
  FaTimes,
  FaYoutube,
  FaMinus,
  FaPlus,
  FaSearch
} from "react-icons/fa";
import Header from "./Header";
import Footer from "./Footer";
import Scroll from "./Scroll";
import MobileNavigation from "./MobileNavigation";
import CrackersLoader from "./CrackersLoader";
import FloatingCart from "./FloatingCart";
import "./ProductDetail.css";

const ProductDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [inputValue, setInputValue] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  const [cartMessage, setCartMessage] = useState(null);
  const [activeTab, setActiveTab] = useState("description");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState([]);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [productPrice, setProductPrice] = useState(0);
  const [productMrp, setProductMrp] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 768);
  const [error, setError] = useState(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [showFloatingCart, setShowFloatingCart] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [isNonDiscount, setIsNonDiscount] = useState(false);
  
  const imageContainerRef = useRef(null);
  const quantityInputRef = useRef(null);
  const autoScrollTimerRef = useRef(null);

  // Toggle floating cart
  const toggleFloatingCart = () => {
    setShowFloatingCart(!showFloatingCart);
  };

  // Get total count of UNIQUE products in cart
  const getTotalItems = () => {
    return Object.keys(counts).length;
  };

  // Load cart items from localStorage
  useEffect(() => {
    const loadCartItems = () => {
      const items = JSON.parse(localStorage.getItem("cartItems") || "[]");
      setCartItems(items);
      
      // Load counts
      const savedCounts = {};
      items.forEach(item => {
        savedCounts[item.product_id] = item.count;
      });
      setCounts(savedCounts);
    };
    loadCartItems();

    const handleCartUpdate = () => {
      loadCartItems();
    };
    window.addEventListener("cartUpdated", handleCartUpdate);
    window.addEventListener("storage", handleCartUpdate);

    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
      window.removeEventListener("storage", handleCartUpdate);
    };
  }, []);

  // Fetch discount percentage from home.php
  useEffect(() => {
    const fetchDiscount = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}home.php`);
        if (response.data && response.data.body) {
          const discountValue = response.data.body.discount_value?.[0]?.discount_value || 0;
          setDiscountPercentage(parseFloat(discountValue));
        }
      } catch (error) {
        console.error("Error fetching discount:", error);
      }
    };
    fetchDiscount();
  }, [API_BASE_URL]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-scroll images
  useEffect(() => {
    if (images.length > 1 && isAutoScrolling) {
      autoScrollTimerRef.current = setInterval(() => {
        const nextIndex = (currentImageIndex + 1) % images.length;
        setCurrentImageIndex(nextIndex);
        setSelectedImage(images[nextIndex]);
        setZoomImage(images[nextIndex]);
      }, 3000);
    }

    return () => {
      if (autoScrollTimerRef.current) {
        clearInterval(autoScrollTimerRef.current);
      }
    };
  }, [images, isAutoScrolling, currentImageIndex]);

  // Fetch product details
  useEffect(() => {
    const productId = id || location.state?.productId || location.state?.product_id;
    
    if (!productId) {
      setError("Product ID not found");
      setIsLoading(false);
      return;
    }

    const fetchProductDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // First fetch product details
        const response = await axios.post(`${API_BASE_URL}product_detail.php`, {
          product_id: productId
        });

        console.log("Product Detail Response:", response.data);

        if (response.data && response.data.head && response.data.head.code === 200) {
          const productData = response.data.body;
          
          // Now fetch category details to get non_discount status
          let categoryNonDiscount = "0";
          try {
            const categoryResponse = await axios.post(`${API_BASE_URL}category_with_products.php`);
            if (categoryResponse.data && categoryResponse.data.head && categoryResponse.data.head.code === 200) {
              const categories = categoryResponse.data.body || [];
              const foundCategory = categories.find(cat => 
                String(cat.category_id) === String(productData.category_id)
              );
              if (foundCategory) {
                categoryNonDiscount = foundCategory.non_discount || "0";
                console.log("Category non_discount:", categoryNonDiscount);
              }
            }
          } catch (catError) {
            console.error("Error fetching category details:", catError);
            // If we can't fetch category details, try to use the category data from product response
            if (productData.category_non_discount !== undefined) {
              categoryNonDiscount = productData.category_non_discount || "0";
            }
          }
          
          setProduct(productData);
          
          // Check if category is non-discount
          const isCategoryNonDisc = categoryNonDiscount === "1" || categoryNonDiscount === 1;
          const isNonDisc = isCategoryNonDisc;
          
          setIsNonDiscount(isNonDisc);
          
          // Set images - Include ALL 3 images
          const productImages = [];
          if (productData.image1 && productData.image1 !== '') {
            productImages.push(productData.image1);
          }
          if (productData.image2 && productData.image2 !== '') {
            productImages.push(productData.image2);
          }
          if (productData.image3 && productData.image3 !== '') {
            productImages.push(productData.image3);
          }
          // If no images, use placeholder
          if (productImages.length === 0) {
            productImages.push(`${API_BASE_URL}uploads/noimages.jpg`);
          }
          setImages(productImages);
          setSelectedImage(productImages[0]);
          setZoomImage(productImages[0]);
          
          // Set prices - only apply discount if category is NOT non-discount
          const mrp = parseFloat(productData.mrp_rate) || 0;
          let discountedPrice = mrp;
          
          if (!isNonDisc && discountPercentage > 0) {
            const discountAmount = (discountPercentage * mrp) / 100;
            discountedPrice = mrp - discountAmount;
          }
          
          setProductMrp(mrp);
          setProductPrice(discountedPrice);
          
          // Check if product is already in cart
          const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
          const existing = cartItems.find(item => item.product_id === productData.product_id);
          if (existing) {
            setQuantity(existing.count);
            setInputValue(existing.count.toString());
          } else {
            setQuantity(1);
            setInputValue('');
          }
          
          // Fetch related products with their category non-discount info
          if (productData.category_id) {
            await fetchRelatedProducts(productData.category_id, productId);
          }
        } else {
          setError(response.data?.head?.msg || "Product not found");
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
        setError("Failed to load product details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductDetails();
  }, [id, navigate, API_BASE_URL, discountPercentage]);

  // Fetch related products
  const fetchRelatedProducts = async (categoryId, currentProductId) => {
    try {
      const response = await axios.post(`${API_BASE_URL}product_list.php`, {
        category_id: categoryId
      });

      if (response.data && response.data.head && response.data.head.code === 200) {
        const products = response.data.body || [];
        const filtered = products
          .filter(p => p.product_id !== currentProductId)
          .slice(0, 8);
        
        // Fetch categories to get non_discount status for each category
        let categoriesData = [];
        try {
          const categoryResponse = await axios.post(`${API_BASE_URL}category_with_products.php`);
          if (categoryResponse.data && categoryResponse.data.head && categoryResponse.data.head.code === 200) {
            categoriesData = categoryResponse.data.body || [];
          }
        } catch (catError) {
          console.error("Error fetching categories for related products:", catError);
        }
        
        // Map products with their category non-discount status
        const productsWithDiscountInfo = filtered.map(p => {
          const foundCategory = categoriesData.find(cat => 
            String(cat.category_id) === String(p.category_id)
          );
          const catNonDiscount = foundCategory?.non_discount || "0";
          const isNonDisc = catNonDiscount === "1" || catNonDiscount === 1;
          return { ...p, isNonDiscount: isNonDisc };
        });
        
        setRelatedProducts(productsWithDiscountInfo);
      }
    } catch (error) {
      console.error("Error fetching related products:", error);
    }
  };

  // Handle quantity change - like Shop page
  const handleQuantityChange = (value) => {
    setInputValue(value);
    
    if (value === '') {
      return;
    }
    
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      setQuantity(numValue);
    }
  };

  const handleQuantityBlur = (value) => {
    if (value === '' || isNaN(parseInt(value, 10)) || parseInt(value, 10) < 1) {
      setQuantity(1);
      setInputValue('');
      // Remove from cart if quantity is 0 or empty
      if (product) {
        const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
        const existingIndex = cartItems.findIndex(item => item.product_id === product.product_id);
        if (existingIndex !== -1) {
          cartItems.splice(existingIndex, 1);
          localStorage.setItem("cartItems", JSON.stringify(cartItems));
          localStorage.removeItem(`count_${product.product_id}`);
          localStorage.setItem("cartCount", cartItems.length);
          window.dispatchEvent(new Event("cartUpdated"));
          window.dispatchEvent(new Event("storage"));
        }
      }
    }
  };

  // Handle add to cart
  const handleAddToCart = () => {
    if (!product) return;
    
    const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
    const existingIndex = cartItems.findIndex(item => item.product_id === product.product_id);
    
    // If product already in cart, just update the quantity
    if (existingIndex !== -1) {
      const newQty = quantity || 1;
      cartItems[existingIndex].count = newQty;
      localStorage.setItem(`count_${product.product_id}`, newQty);
      localStorage.setItem("cartItems", JSON.stringify(cartItems));
      
      localStorage.setItem("cartCount", cartItems.length);
      
      setCartMessage("Cart updated!");
      setTimeout(() => setCartMessage(null), 3000);
      window.dispatchEvent(new Event("cartUpdated"));
      window.dispatchEvent(new Event("storage"));
      return;
    }
    
    // If not in cart, add new item
    const mrp = parseFloat(product.mrp_rate) || 0;
    let discountedPrice = mrp;
    
    // Only apply discount if category is NOT non-discount
    if (!isNonDiscount && discountPercentage > 0) {
      const discountAmount = (discountPercentage * mrp) / 100;
      discountedPrice = mrp - discountAmount;
    }
    
    cartItems.push({
      product_id: product.product_id,
      product_name: product.product_name,
      image: product.image1,
      oldprice: mrp.toString(),
      price: discountedPrice.toString(),
      count: quantity || 1,
      is_non_discount: isNonDiscount ? "1" : "0"
    });
    
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    localStorage.setItem(`count_${product.product_id}`, quantity || 1);
    
    localStorage.setItem("cartCount", cartItems.length);
    
    setInputValue((quantity || 1).toString());
    setQuantity(quantity || 1);
    
    setCartMessage("Product added to cart successfully!");
    setTimeout(() => setCartMessage(null), 3000);
    
    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("storage"));
  };

  // Handle image navigation
  const nextImage = () => {
    setIsAutoScrolling(false);
    if (images.length > 1) {
      const nextIndex = (currentImageIndex + 1) % images.length;
      setCurrentImageIndex(nextIndex);
      setSelectedImage(images[nextIndex]);
      setZoomImage(images[nextIndex]);
    }
    clearTimeout(autoScrollTimerRef.current);
    setTimeout(() => {
      setIsAutoScrolling(true);
    }, 5000);
  };

  const prevImage = () => {
    setIsAutoScrolling(false);
    if (images.length > 1) {
      const prevIndex = (currentImageIndex - 1 + images.length) % images.length;
      setCurrentImageIndex(prevIndex);
      setSelectedImage(images[prevIndex]);
      setZoomImage(images[prevIndex]);
    }
    clearTimeout(autoScrollTimerRef.current);
    setTimeout(() => {
      setIsAutoScrolling(true);
    }, 5000);
  };

  const handleThumbnailClick = (img, index) => {
    setIsAutoScrolling(false);
    setSelectedImage(img);
    setZoomImage(img);
    setCurrentImageIndex(index);
    clearTimeout(autoScrollTimerRef.current);
    setTimeout(() => {
      setIsAutoScrolling(true);
    }, 5000);
  };

  // Handle product click for related products
  const handleProductClick = (productId) => {
    navigate(`/productdetail/${productId}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Navigate to Shop page with category filter
  const navigateToShopWithCategory = (categoryId) => {
    navigate(`/shop?category=${categoryId}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Format price
  const formatPrice = (price) => {
    return parseFloat(price || 0).toFixed(2);
  };

  // Get price with discount for related products
  const getPriceWithDiscount = (mrpRate, isNonDisc) => {
    const mrp = parseFloat(mrpRate) || 0;
    let discountedPrice = mrp;
    let hasDiscount = false;
    
    // Only apply discount if NOT non-discount and discountPercentage > 0
    if (!isNonDisc && discountPercentage > 0) {
      const discountAmount = (discountPercentage * mrp) / 100;
      discountedPrice = mrp - discountAmount;
      hasDiscount = true;
    }
    
    return {
      original: Math.round(mrp * 100) / 100,
      discounted: Math.round(discountedPrice * 100) / 100,
      hasDiscount: hasDiscount,
      isNonDiscount: isNonDisc
    };
  };

  if (isLoading) {
    return <CrackersLoader />;
  }

  if (error || !product) {
    return (
      <div className="pd-page">
        <Header />
        <div className="pd-not-found">
          <div className="pd-not-found-icon">😕</div>
          <h2>{error || "Product Not Found"}</h2>
          <p>The product you're looking for doesn't exist or has been removed.</p>
          <Link to="/" className="pd-back-btn">Go Back Home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="pd-page">
      <Header />

      {/* Cart Floating Icon */}
      {getTotalItems() > 0 && (
        <div className="pd-cart-floating-icon" onClick={toggleFloatingCart}>
          <span className="pd-cart-icon-badge">{getTotalItems()}</span>
          <FaShoppingCart className="pd-cart-icon" />
        </div>
      )}

      {/* Floating Cart Sidebar */}
      <FloatingCart 
        isOpen={showFloatingCart}
        onClose={toggleFloatingCart}
        cartItems={cartItems}
        discountvalue={discountPercentage}
        onUpdateCart={setCartItems}
        onCountsUpdate={setCounts}
      />
      
      {/* Breadcrumb */}
      <div className="pd-breadcrumb">
        <div className="pd-container">
          <Link to="/">Home</Link>
          <span className="pd-breadcrumb-sep">›</span>
          <Link to="/quick">Products</Link>
          <span className="pd-breadcrumb-sep">›</span>
          {product.category_name && (
            <>
              <button 
                className="pd-breadcrumb-link"
                onClick={() => navigateToShopWithCategory(product.category_id)}
              >
                {product.category_name}
              </button>
              <span className="pd-breadcrumb-sep">›</span>
            </>
          )}
          <span className="pd-breadcrumb-current">{product.product_name}</span>
        </div>
      </div>

      {/* Cart Message */}
      {cartMessage && (
        <div className="pd-cart-message">
          <FaCheckCircle className="pd-cart-message-icon" />
          <span>{cartMessage}</span>
        </div>
      )}

      {/* Main Product Section */}
      <div className="pd-main-section">
        <div className="pd-container">
          <div className="pd-grid">
            {/* Left - Images */}
            <div className="pd-image-section">
              <div className="pd-main-image-container">
                <div className="pd-main-image-wrapper" ref={imageContainerRef}>
                  {images.length > 1 && (
                    <>
                      <button className="pd-nav-btn pd-nav-prev" onClick={prevImage}>
                        <FaArrowLeft />
                      </button>
                      <button className="pd-nav-btn pd-nav-next" onClick={nextImage}>
                        <FaArrowRight />
                      </button>
                    </>
                  )}
                  <img
                    src={selectedImage || product.image1}
                    alt={product.product_name}
                    className="pd-main-image"
                    onClick={() => setShowZoomModal(true)}
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = `${API_BASE_URL}uploads/noimages.jpg`;
                    }}
                  />
                  {/* Only show discount badge if category is NOT non-discount */}
                  {!isNonDiscount && discountPercentage > 0 && (
                    <span className="pd-discount-badge">
                      {discountPercentage}% OFF
                    </span>
                  )}
                  {product.new_arrival === "1" && (
                    <span className="pd-new-badge">NEW</span>
                  )}
                  <button className="pd-zoom-btn" onClick={() => setShowZoomModal(true)}>
                    <FaSearch />
                  </button>
                  {images.length > 1 && (
                    <div className="pd-image-counter">
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  )}
                </div>
                
                {/* Thumbnails - Show ALL images */}
                {images.length > 1 && (
                  <div className="pd-thumbnails">
                    {images.map((img, index) => (
                      <div
                        key={index}
                        className={`pd-thumbnail ${selectedImage === img ? 'active' : ''}`}
                        onClick={() => handleThumbnailClick(img, index)}
                      >
                        <img src={img} alt={`Thumbnail ${index + 1}`} loading="lazy" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right - Product Info */}
            <div className="pd-info-section">
              <h1 className="pd-product-title">{product.product_name}</h1>
              
              <div className="pd-price-section">
                <div className="pd-prices">
                  {!isNonDiscount && discountPercentage > 0 ? (
                    <>
                      <del className="pd-old-price">
                        <FaRupeeSign /> {formatPrice(productMrp)}
                      </del>
                      <span className="pd-current-price">
                        <FaRupeeSign /> {formatPrice(productPrice)}
                      </span>
                      <span className="pd-discount-label">
                        Save {discountPercentage}%
                      </span>
                    </>
                  ) : (
                    <span className="pd-current-price">
                      <FaRupeeSign /> {formatPrice(productPrice)}
                    </span>
                  )}
                </div>
              </div>

              {product.description && (
                <div className="pd-description-short">
                  <p>{product.description}</p>
                </div>
              )}

              {/* Quantity Section - Like Shop page */}
              <div className="pd-quantity-section">
                <div className="pd-quantity-label">Quantity</div>
                <div className="pd-quantity-control">
                  <button 
                    className="pd-qty-btn" 
                    onClick={() => {
                      const currentQty = parseInt(inputValue) || 0;
                      const newQty = Math.max(0, currentQty - 1);
                      if (newQty === 0) {
                        setInputValue('');
                        setQuantity(1);
                        // Remove from cart
                        const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
                        const existingIndex = cartItems.findIndex(item => item.product_id === product.product_id);
                        if (existingIndex !== -1) {
                          cartItems.splice(existingIndex, 1);
                          localStorage.setItem("cartItems", JSON.stringify(cartItems));
                          localStorage.removeItem(`count_${product.product_id}`);
                          localStorage.setItem("cartCount", cartItems.length);
                          window.dispatchEvent(new Event("cartUpdated"));
                          window.dispatchEvent(new Event("storage"));
                        }
                      } else {
                        setInputValue(newQty.toString());
                        setQuantity(newQty);
                        // Update cart
                        const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
                        const existingIndex = cartItems.findIndex(item => item.product_id === product.product_id);
                        if (existingIndex !== -1) {
                          cartItems[existingIndex].count = newQty;
                          localStorage.setItem(`count_${product.product_id}`, newQty);
                          localStorage.setItem("cartItems", JSON.stringify(cartItems));
                          localStorage.setItem("cartCount", cartItems.length);
                          window.dispatchEvent(new Event("cartUpdated"));
                          window.dispatchEvent(new Event("storage"));
                        }
                      }
                    }}
                  >
                    <FaMinus />
                  </button>
                  <input
                    type="number"
                    ref={quantityInputRef}
                    className="pd-qty-input"
                    value={inputValue}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    onBlur={(e) => handleQuantityBlur(e.target.value)}
                    min="0"
                    step="1"
                    placeholder="0"
                  />
                  <button 
                    className="pd-qty-btn" 
                    onClick={() => {
                      const currentQty = parseInt(inputValue) || 0;
                      const newQty = currentQty + 1;
                      setInputValue(newQty.toString());
                      setQuantity(newQty);
                      // Update cart
                      const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
                      const existingIndex = cartItems.findIndex(item => item.product_id === product.product_id);
                      if (existingIndex !== -1) {
                        cartItems[existingIndex].count = newQty;
                        localStorage.setItem(`count_${product.product_id}`, newQty);
                        localStorage.setItem("cartItems", JSON.stringify(cartItems));
                        localStorage.setItem("cartCount", cartItems.length);
                        window.dispatchEvent(new Event("cartUpdated"));
                        window.dispatchEvent(new Event("storage"));
                      }
                    }}
                  >
                    <FaPlus />
                  </button>
                </div>
              </div>

              <div className="pd-action-section">
                <button 
                  className="pd-add-to-cart-btn"
                  onClick={handleAddToCart}
                >
                  <FaShoppingCart className="pd-btn-icon" />
                  Add to Cart
                </button>
              </div>

              {/* YouTube Video if available */}
              {product.youtube_url && (
                <div className="pd-video-section">
                  <button 
                    className="pd-video-btn"
                    onClick={() => {
                      const videoIdMatch = product.youtube_url.match(/(?:shorts\/|watch\?v=)([a-zA-Z0-9_-]+)/);
                      if (videoIdMatch && videoIdMatch[1]) {
                        setZoomImage(`https://www.youtube.com/embed/${videoIdMatch[1]}`);
                        setShowZoomModal(true);
                      }
                    }}
                  >
                    <FaYoutube className="pd-video-icon" />
                    Watch Product Video
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="pd-tabs-section">
        <div className="pd-container">
          <div className="pd-tabs-header">
            <button
              className={`pd-tab-btn ${activeTab === "description" ? "active" : ""}`}
              onClick={() => setActiveTab("description")}
            >
              Description
            </button>
            <button
              className={`pd-tab-btn ${activeTab === "specifications" ? "active" : ""}`}
              onClick={() => setActiveTab("specifications")}
            >
              Specifications
            </button>
          </div>
          <div className="pd-tabs-content">
            {activeTab === "description" && (
              <div className="pd-tab-panel">
                <div className="pd-description-full">
                  <p>{product.description || "No detailed description available for this product."}</p>
                </div>
              </div>
            )}
            {activeTab === "specifications" && (
              <div className="pd-tab-panel">
                <div className="pd-specifications">
                  <div className="pd-spec-row">
                    <span className="pd-spec-label">Product Name</span>
                    <span className="pd-spec-value">{product.product_name}</span>
                  </div>
                  <div className="pd-spec-row">
                    <span className="pd-spec-label">Category</span>
                    <span className="pd-spec-value">
                      <button 
                        className="pd-spec-category-link"
                        onClick={() => navigateToShopWithCategory(product.category_id)}
                      >
                        {product.category_name || "N/A"}
                      </button>
                    </span>
                  </div>
                  <div className="pd-spec-row">
                    <span className="pd-spec-label">Price</span>
                    <span className="pd-spec-value">
                      {!isNonDiscount && discountPercentage > 0 ? (
                        <>
                          <del style={{ color: '#94a3b8', marginRight: '8px' }}>
                            <FaRupeeSign /> {formatPrice(productMrp)}
                          </del>
                          <span style={{ color: '#dc2626', fontWeight: 'bold' }}>
                            <FaRupeeSign /> {formatPrice(productPrice)}
                          </span>
                        </>
                      ) : (
                        <span>
                          <FaRupeeSign /> {formatPrice(productPrice)}
                        </span>
                      )}
                    </span>
                  </div>
                  {product.qty_per_box && (
                    <div className="pd-spec-row">
                      <span className="pd-spec-label">Qty per Box</span>
                      <span className="pd-spec-value">{product.qty_per_box}</span>
                    </div>
                  )}
                  {product.day === "1" && (
                    <div className="pd-spec-row">
                      <span className="pd-spec-label">Type</span>
                      <span className="pd-spec-value">☀️ Day</span>
                    </div>
                  )}
                  {product.night === "1" && (
                    <div className="pd-spec-row">
                      <span className="pd-spec-label">Type</span>
                      <span className="pd-spec-value">🌙 Night</span>
                    </div>
                  )}
                  {product.kids === "1" && (
                    <div className="pd-spec-row">
                      <span className="pd-spec-label">Type</span>
                      <span className="pd-spec-value">🧒 Kids</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div className="pd-related-section">
          <div className="pd-container">
            <div className="pd-related-header">
              <h2 className="pd-related-title">Related Products</h2>
              <button 
                className="pd-related-view-all"
                onClick={() => navigateToShopWithCategory(product.category_id)}
              >
                View All →
              </button>
            </div>
            <div className="pd-related-grid">
              {relatedProducts.map((product) => {
                const isNonDisc = product.isNonDiscount || false;
                const priceInfo = getPriceWithDiscount(product.mrp_rate, isNonDisc);
                
                return (
                  <div 
                    key={product.product_id} 
                    className="pd-related-card"
                    onClick={() => handleProductClick(product.product_id)}
                  >
                    <div className="pd-related-image-wrapper">
                      <img
                        src={product.product_image || `${API_BASE_URL}uploads/noimages.jpg`}
                        alt={product.product_name}
                        className="pd-related-image"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = `${API_BASE_URL}uploads/noimages.jpg`;
                        }}
                      />
                      {priceInfo.hasDiscount && (
                        <span className="pd-related-discount">{Math.round(discountPercentage)}% OFF</span>
                      )}
                      {product.new_arrival === "1" && (
                        <span className="pd-related-new">NEW</span>
                      )}
                    </div>
                    <div className="pd-related-info">
                      <h4 className="pd-related-name">{product.product_name}</h4>
                      <div className="pd-related-price">
                        {priceInfo.hasDiscount ? (
                          <>
                            <del className="pd-related-old">
                              <FaRupeeSign /> {priceInfo.original.toFixed(2)}
                            </del>
                            <span className="pd-related-current">
                              <FaRupeeSign /> {priceInfo.discounted.toFixed(2)}
                            </span>
                          </>
                        ) : (
                          <span className="pd-related-current">
                            <FaRupeeSign /> {priceInfo.original.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <button 
                        className="pd-related-cart-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProductClick(product.product_id);
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Zoom Modal */}
      {showZoomModal && (
        <div className="pd-zoom-modal" onClick={() => setShowZoomModal(false)}>
          <div className="pd-zoom-content" onClick={(e) => e.stopPropagation()}>
            <button className="pd-zoom-close" onClick={() => setShowZoomModal(false)}>
              <FaTimes />
            </button>
            {zoomImage && zoomImage.includes('youtube.com/embed') ? (
              <div className="pd-video-container">
                <iframe
                  src={zoomImage}
                  title="Product Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="pd-video-iframe"
                />
              </div>
            ) : (
              <img src={zoomImage || selectedImage} alt="Zoom" className="pd-zoom-image" />
            )}
          </div>
        </div>
      )}

      <Footer />
      <MobileNavigation />
      <Scroll />
    </div>
  );
};

export default ProductDetail;